import React, { useState } from "react";
import { toResult, type Result, type CheckStatus } from "@sequesign/verifier-browser";

import { bundleToLoadedReceipt } from "../load/bundle.js";
import { runDemoVerification } from "../verify/run-demo.js";
import { runSerialized } from "../verify/serialize.js";
import { LevelSummary } from "./LevelBadges.js";
import { ProcessFlow, type FlowConfig } from "./ProcessFlow.js";

type CardKey = "conformant" | "violation";

// Structural config the hero renders — supplied by each demo's scenario so the
// component is fully reskinnable (Torbridge invoices, Veralta clinical, …).
export interface HeroCardConfig {
  // Key into the `bundles` map: the recorded package this card verifies.
  bundleId: string;
  tag: string;
  amount: string;
  headline: string;
  line: string;
  expect: string;
}
export interface HeroConfig {
  eyebrow: string;
  headline: string;
  sub: string;
  verifyLabel: string;
  verifyingLabel: string;
  reverifyLabel: string;
  browserNote: string;
  cards: { conformant: HeroCardConfig; violation: HeroCardConfig };
}
export interface HeroCta {
  label: string;
  sub: string;
  onClick: () => void;
}

const STATUS_MARK: Record<CheckStatus, string> = {
  pass: "✓",
  fail: "✕",
  partial: "◐",
  not_evaluated: "–"
};

// The landing hero: the two-sentence hook, the process strip, two side-by-side
// recorded receipts a visitor verifies in-browser with one click, then CTAs
// into the deeper flows. All copy + the card→bundle wiring come from props, so
// the same component skins both the Torbridge and Veralta Overviews.
export function HeroPanel({
  hero,
  flow,
  bundles,
  ctas
}: {
  hero: HeroConfig;
  flow: FlowConfig;
  // bundleId -> flat { relPath: contents } recorded package.
  bundles: Record<string, Record<string, string>>;
  ctas: HeroCta[];
}) {
  const [results, setResults] = useState<Partial<Record<CardKey, Result>>>({});
  const [errors, setErrors] = useState<Partial<Record<CardKey, string>>>({});
  // One verification at a time: runDemoVerification installs into a SHARED
  // in-memory virtual filesystem (install clears it first), so two concurrent
  // verifications would read each other's files. `busy` names the card whose
  // verification is in flight and disables the other card's button meanwhile.
  const [busy, setBusy] = useState<CardKey | null>(null);

  async function verify(key: CardKey, card: HeroCardConfig) {
    if (busy) return;
    setBusy(key);
    setErrors((e) => ({ ...e, [key]: undefined }));
    try {
      const bundle = bundles[card.bundleId];
      if (!bundle) throw new Error(`No recorded bundle for ${card.bundleId}`);
      const loaded = bundleToLoadedReceipt(card.bundleId, bundle);
      const started = performance.now();
      // Serialized process-wide (shared virtual FS): never overlap another
      // card's run or a still-in-flight recorded-layer verification.
      const { report } = await runSerialized(() => runDemoVerification(loaded));
      const totalMs = performance.now() - started;
      const result = toResult(report, { totalMs, verifierVersion: "demo", commit: "demo" });
      setResults((r) => ({ ...r, [key]: result }));
    } catch (err) {
      setErrors((e) => ({ ...e, [key]: err instanceof Error ? err.message : String(err) }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="hero">
      <div className="hero-head">
        <div className="hero-eyebrow">{hero.eyebrow}</div>
        <h1 className="hero-headline">{hero.headline}</h1>
        <p className="hero-sub">{hero.sub}</p>
      </div>

      <ProcessFlow flow={flow} />

      <div className="hero-cards">
        <HeroReceiptCard
          card={hero.cards.conformant}
          variant="conformant"
          labels={hero}
          result={results.conformant ?? null}
          error={errors.conformant ?? null}
          busy={busy === "conformant"}
          disabled={busy !== null && busy !== "conformant"}
          onVerify={() => verify("conformant", hero.cards.conformant)}
        />
        <HeroReceiptCard
          card={hero.cards.violation}
          variant="violation"
          labels={hero}
          result={results.violation ?? null}
          error={errors.violation ?? null}
          busy={busy === "violation"}
          disabled={busy !== null && busy !== "violation"}
          onVerify={() => verify("violation", hero.cards.violation)}
        />
      </div>

      <p className="hero-browser-note">{hero.browserNote}</p>

      <div className="hero-ctas">
        {ctas.map((cta, i) => (
          <button className="hero-cta" key={i} onClick={cta.onClick}>
            <span className="hero-cta-label">{cta.label}</span>
            <span className="hero-cta-sub">{cta.sub}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function HeroReceiptCard({
  card,
  variant,
  labels,
  result,
  error,
  busy,
  disabled,
  onVerify
}: {
  card: HeroCardConfig;
  variant: CardKey;
  labels: Pick<HeroConfig, "verifyLabel" | "verifyingLabel" | "reverifyLabel">;
  result: Result | null;
  error: string | null;
  busy: boolean;
  disabled: boolean;
  onVerify: () => void;
}) {
  const verified = result !== null;
  return (
    <div className={`hero-card hero-card-${variant}`}>
      <div className="hero-card-top">
        <span className={`hero-card-tag hero-card-tag-${variant}`}>{card.tag}</span>
        <span className="hero-card-amount">{card.amount}</span>
      </div>
      <h2 className="hero-card-headline">{card.headline}</h2>
      <p className="hero-card-line">{card.line}</p>

      {!verified && <p className="hero-card-expect">{card.expect}</p>}
      {error && <div className="hero-card-error">Verification error: {error}</div>}
      {result && <HeroVerdict result={result} />}

      <button className="hero-verify-btn" onClick={onVerify} disabled={busy || disabled}>
        {busy ? labels.verifyingLabel : verified ? labels.reverifyLabel : labels.verifyLabel}
      </button>
    </div>
  );
}

function HeroVerdict({ result }: { result: Result }) {
  const valid = result.valid;
  const conformant = result.conformant;

  const headline = !valid
    ? "Verification failed"
    : conformant === false
      ? "Verified: violation recorded"
      : "Receipt verified";

  const detail = !valid
    ? `Rejected: ${result.reason ?? "see the failed check below."}`
    : conformant === false
      ? "Integrity, signatures, and the bound mandate all check out, and the receipt honestly records that the work did NOT conform (conformant: false)."
      : "Integrity, signatures, evidence schema, and mandate conformance all check out.";

  // Valid-but-non-conformant is the honesty story, not a failure — give it its
  // own "warn" styling rather than the red invalid treatment.
  const verdictClass = !valid ? "invalid" : conformant === false ? "warn" : "valid";

  return (
    <div className={`hero-verdict hero-verdict-${verdictClass}`}>
      <div className="hero-verdict-head">
        <span className="hero-verdict-mark" aria-hidden="true">
          {valid ? "✓" : "✕"}
        </span>
        <div>
          <div className="hero-verdict-headline">{headline}</div>
          <div className="hero-verdict-detail">{detail}</div>
        </div>
      </div>

      <LevelSummary result={result} />

      {result.trustAnchorMode === "self" && (
        <p className="hero-verdict-anchor">
          Integrity self-check: verified against the witness key carried in this recorded receipt,
          not an independent third-party anchor. The <strong>Run live</strong> tab anchors to the
          witness's published key for a genuine third-party check.
        </p>
      )}

      <details className="hero-checks">
        <summary>Show the {result.checks.length} checks the verifier ran</summary>
        <ul className="hero-check-list">
          {result.checks.map((c) => (
            <li key={c.id} className={`hero-check hero-check-${c.status}`}>
              <span className="hero-check-mark" aria-hidden="true">
                {STATUS_MARK[c.status]}
              </span>
              <span className="hero-check-detail">{c.detail}</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
